/**
 * CreateActionFields — presentational schema-driven field list for a create
 * action's editable properties. Pure UI: it renders each field from the given
 * values + schema and reports changes via onChange. All edit-state ownership
 * (values, dirty, save) lives in the caller (CreateActionEditForm for the
 * standalone card, MergedCreateActionCard for the merged card).
 */

import { useCallback } from 'react';

import { CrmPropertyField } from '@/lib/crm/components/fields/CrmPropertyField';
import type {
  PropertySchemaMap,
  PropertyTypeSchema,
} from '@/lib/crm/hooks/usePropertySchema';

import { formatValue } from './formatValue';
import { formatPropertyLabel } from './shared';

// ─── Schema-driven field input ──────────────────────────────────────────────

interface SchemaFieldInputProps {
  propertyKey: string;
  propSchema: PropertyTypeSchema | undefined;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
}

/**
 * Renders the appropriate editable input for a create-form property:
 * - Schema-backed properties → CrmPropertyField (currency, date, select, etc.)
 * - Unknown properties → plain text input fallback
 */
export function SchemaFieldInput({
  propertyKey,
  propSchema,
  value,
  onChange,
}: SchemaFieldInputProps) {
  const handleSchemaChange = useCallback(
    (v: unknown) => {
      onChange(propertyKey, v);
    },
    [onChange, propertyKey]
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(propertyKey, e.target.value || null);
    },
    [onChange, propertyKey]
  );

  if (propSchema) {
    return (
      <CrmPropertyField
        propertyType={propSchema}
        value={value as string | number | object | null | undefined}
        onChange={handleSchemaChange}
      />
    );
  }

  // Fallback: plain text input for properties without a schema.
  //
  // Safeguard: never coerce the raw value with String() — an object value (a
  // currency `{amount, unit}`, or any structured property that reaches this
  // path before/without a schema) would render as the literal "[object
  // Object]". formatValue() stringifies objects as readable JSON and passes
  // primitives through unchanged, so the field is always legible. Structured
  // values also can't be meaningfully edited as free text, so the input is
  // read-only for them — this prevents typing a string over an object and
  // silently corrupting the payload on save.
  const isStructuredValue = typeof value === 'object' && value !== null;
  return (
    <div className='flex flex-col gap-1.5'>
      <label className='text-xs font-medium text-fg-tertiary'>
        {formatPropertyLabel(propertyKey)}
      </label>
      <input
        type='text'
        value={formatValue(value)}
        onChange={handleTextChange}
        readOnly={isStructuredValue}
        aria-label={formatPropertyLabel(propertyKey)}
        className='w-full rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm text-fg-primary placeholder:text-fg-tertiary read-only:text-fg-tertiary focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent'
      />
    </div>
  );
}

// ─── Field list ───────────────────────────────────────────────────────────────

interface CreateActionFieldsProps {
  fieldKeys: string[];
  values: Record<string, unknown>;
  schema: PropertySchemaMap;
  onChange: (key: string, value: unknown) => void;
}

export function CreateActionFields({
  fieldKeys,
  values,
  schema,
  onChange,
}: CreateActionFieldsProps) {
  return (
    <div className='space-y-3'>
      {fieldKeys.map(key => (
        <SchemaFieldInput
          key={key}
          propertyKey={key}
          propSchema={schema[key]}
          value={values[key]}
          onChange={onChange}
        />
      ))}
    </div>
  );
}
